import { Suspense, useEffect } from 'react';
import { useHashRoute } from './hooks/useHashRoute';
import { usePrefs, ACCENT_COLORS } from './stores/prefsStore';
import { makeT } from './i18n';
import { findChapter } from './chapters/registry';
import { Header } from './components/layout/Header';
import { Sidebar } from './components/layout/Sidebar';
import { LessonShell } from './components/layout/LessonShell';
import { ErrorBoundary } from './components/ErrorBoundary';
import { Home } from './pages/Home';

export default function App() {
  const { theme, lang, accent, toggleTheme, toggleLang } = usePrefs();
  const [route, nav] = useHashRoute();

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    document.documentElement.setAttribute('lang', lang === 'zh' ? 'zh' : 'en');
    const accentColors = ACCENT_COLORS[accent];
    Object.entries(accentColors).forEach(([k, v]) => {
      document.documentElement.style.setProperty(k, v);
    });
  }, [theme, lang, accent]);

  const t = makeT(lang);

  let content: React.ReactNode;
  if (route === 'home') {
    content = <Home t={t} onNav={nav} />;
  } else {
    const chapter = findChapter(route);
    if (chapter) {
      const Comp = chapter.Component;
      content = (
        <LessonShell chapter={chapter} t={t} onNav={nav}>
          <ErrorBoundary>
            <Suspense fallback={<div style={{ padding: 40 }}>…</div>}>
              <Comp t={t} />
            </Suspense>
          </ErrorBoundary>
        </LessonShell>
      );
    } else {
      content = <Home t={t} onNav={nav} />;
    }
  }

  return (
    <div className="app">
      <Header
        t={t}
        theme={theme}
        lang={lang}
        onToggleTheme={toggleTheme}
        onToggleLang={toggleLang}
        onNavHome={() => nav('home')}
      />
      <Sidebar t={t} route={route} onNav={nav} />
      <main className="main" key={route}>
        {content}
      </main>
    </div>
  );
}
